import { defineStore } from 'pinia';
import { imClient } from '@/services/imClient';
import { useConversationStore } from '@/stores/conversations';
import { useNavigationStore } from '@/stores/navigation';
import { useSessionStore } from '@/stores/session';

const CONTACT_CACHE_MS = 15000;
const APPLICATION_CACHE_MS = 15000;
const FRIEND_APPLICATION_UNPROCESSED = 0;

function normalizeContacts(contacts) {
  const items = Array.isArray(contacts) ? contacts : [];
  const unique = new Map();
  items.forEach(contact => {
    const uid = String(contact.uid || contact.id || '').trim();
    if (!uid) {
      return;
    }
    const normalized = {
      ...contact,
      id: String(contact.id || uid),
      uid
    };
    unique.set(uid, {
      ...(unique.get(uid) || {}),
      ...normalized
    });
  });
  return Array.from(unique.values()).sort((a, b) => a.title.localeCompare(b.title, 'zh-CN'));
}

export const useContactStore = defineStore('contacts', {
  state: () => ({
    contacts: [],
    activeId: '',
    loading: false,
    eventsBound: false,
    refreshSerial: 0,
    refreshPromise: null,
    loadedForUserId: '',
    loadedAt: 0,
    friendApplications: [],
    applicationsLoading: false,
    applicationBusyIds: [],
    applicationsLoadedForUserId: '',
    applicationsLoadedAt: 0,
    lastAddContactResult: null,
    actionError: '',
    actionInfo: ''
  }),
  getters: {
    activeContact(state) {
      return state.contacts.find(item => item.id === state.activeId) || state.contacts[0] || null;
    },
    pendingFriendApplications(state) {
      return state.friendApplications.filter(item => Number(item.handleResult) === FRIEND_APPLICATION_UNPROCESSED);
    },
    pendingApplicationCount(state) {
      return state.friendApplications.filter(item => Number(item.handleResult) === FRIEND_APPLICATION_UNPROCESSED).length;
    }
  },
  actions: {
    bindImEvents() {
      if (this.eventsBound) {
        return;
      }
      this.eventsBound = true;
      imClient.on('contactsChanged', contacts => {
        const nextContacts = Array.isArray(contacts) ? contacts : [];
        if (!nextContacts.length && this.contacts.length) {
          return;
        }
        this.contacts = normalizeContacts(nextContacts);
        if (!this.contacts.find(item => item.id === this.activeId)) {
          this.activeId = this.contacts[0]?.id || '';
        }
      });
      imClient.on('friendApplicationsChanged', () => {
        this.refreshFriendApplications({ force: true, silent: true }).catch(() => {});
      });
    },
    async refreshContacts(options = {}) {
      this.bindImEvents();
      const session = useSessionStore();
      const requestUserId = session.userId;
      const force = Boolean(options.force);
      if (!requestUserId) {
        this.clearContacts();
        return [];
      }
      if (!force
        && this.contacts.length
        && this.loadedForUserId === requestUserId
        && Date.now() - this.loadedAt < CONTACT_CACHE_MS
      ) {
        return this.contacts;
      }
      if (this.refreshPromise && !force) {
        return this.refreshPromise;
      }
      const serial = this.refreshSerial + 1;
      this.refreshSerial = serial;
      this.loading = true;

      this.refreshPromise = (async () => {
        this.contacts = normalizeContacts(imClient.getContactList(false));
        if (!this.contacts.find(item => item.id === this.activeId)) {
          this.activeId = this.contacts[0]?.id || '';
        }
        if (!this.contacts.length || force) {
          let serverContacts = [];
          try {
            serverContacts = await imClient.getServerContactList(requestUserId);
          } catch (error) {
            return this.contacts;
          }
          if (serial !== this.refreshSerial || useSessionStore().userId !== requestUserId) {
            return this.contacts;
          }
          this.contacts = normalizeContacts(serverContacts);
          this.loadedForUserId = requestUserId;
          this.loadedAt = Date.now();
          if (!this.contacts.find(item => item.id === this.activeId)) {
            this.activeId = this.contacts[0]?.id || '';
          }
        }
        window.setTimeout(() => {
          if (serial !== this.refreshSerial || useSessionStore().userId !== requestUserId) {
            return;
          }
          const latest = imClient.getContactList(false);
          if (latest.length || !this.contacts.length) {
            this.contacts = normalizeContacts(latest);
            this.loadedForUserId = requestUserId;
            this.loadedAt = Date.now();
            if (!this.contacts.find(item => item.id === this.activeId)) {
              this.activeId = this.contacts[0]?.id || '';
            }
          }
        }, 1200);
        return this.contacts;
      })();

      try {
        return await this.refreshPromise;
      } finally {
        if (serial === this.refreshSerial) {
          this.loading = false;
          this.refreshPromise = null;
        }
      }
    },
    async addContact(keyword) {
      const session = useSessionStore();
      const value = String(keyword || '').trim();
      this.actionError = '';
      this.actionInfo = '';
      this.lastAddContactResult = null;
      if (!session.userId) {
        this.actionError = '请先登录';
        return false;
      }
      if (!value) {
        this.actionError = '请输入用户ID、手机号或用户名';
        return false;
      }

      this.loading = true;
      try {
        const result = await imClient.addContact(session.userId, value, '添加联系人');
        this.lastAddContactResult = result;
        if (result?.contact) {
          this.contacts = normalizeContacts([...this.contacts, result.contact]);
          this.activeId = result.contact.id;
          this.actionInfo = result.alreadyFriend ? '已经是联系人' : '联系人已添加';
          await this.refreshContacts({ force: true });
          return result;
        }
        if (result?.pending) {
          this.actionInfo = `好友申请已发送给 ${result.targetId}，等待对方在通讯录的好友申请中通过`;
          return result;
        }
        this.actionInfo = '添加联系人请求已提交';
        return result || true;
      } catch (error) {
        this.actionError = error.message || '添加联系人失败';
        return false;
      } finally {
        this.loading = false;
      }
    },
    async refreshFriendApplications(options = {}) {
      this.bindImEvents();
      const session = useSessionStore();
      const requestUserId = session.userId;
      const force = Boolean(options.force);
      if (!requestUserId) {
        this.friendApplications = [];
        this.applicationsLoadedForUserId = '';
        this.applicationsLoadedAt = 0;
        return [];
      }
      if (!force
        && this.applicationsLoadedForUserId === requestUserId
        && Date.now() - this.applicationsLoadedAt < APPLICATION_CACHE_MS
      ) {
        return this.friendApplications;
      }
      this.applicationsLoading = true;
      try {
        const applications = await imClient.getReceivedFriendApplications({ onlyUnhandled: true });
        if (useSessionStore().userId !== requestUserId) {
          return this.friendApplications;
        }
        this.friendApplications = applications;
        this.applicationsLoadedForUserId = requestUserId;
        this.applicationsLoadedAt = Date.now();
        return applications;
      } catch (error) {
        if (!options.silent) {
          this.actionError = error.message || '好友申请加载失败';
        }
        return this.friendApplications;
      } finally {
        this.applicationsLoading = false;
      }
    },
    async acceptFriendApplication(application) {
      const fromUserID = String(application?.fromUserID || application || '').trim();
      const busyId = String(application?.id || fromUserID);
      if (!fromUserID) {
        this.actionError = '缺少申请人ID';
        return false;
      }
      this.actionError = '';
      this.actionInfo = '';
      this.applicationBusyIds = Array.from(new Set([...this.applicationBusyIds, busyId]));
      try {
        await imClient.acceptFriendApplication(fromUserID, '已同意');
        this.friendApplications = this.friendApplications.filter(item => item.fromUserID !== fromUserID);
        this.actionInfo = '已通过好友申请';
        await this.refreshContacts({ force: true });
        await this.refreshFriendApplications({ force: true, silent: true });
        return true;
      } catch (error) {
        this.actionError = error.message || '同意好友申请失败';
        return false;
      } finally {
        this.applicationBusyIds = this.applicationBusyIds.filter(item => item !== busyId);
      }
    },
    async refuseFriendApplication(application) {
      const fromUserID = String(application?.fromUserID || application || '').trim();
      const busyId = String(application?.id || fromUserID);
      if (!fromUserID) {
        this.actionError = '缺少申请人ID';
        return false;
      }
      this.actionError = '';
      this.actionInfo = '';
      this.applicationBusyIds = Array.from(new Set([...this.applicationBusyIds, busyId]));
      try {
        await imClient.refuseFriendApplication(fromUserID, '已拒绝');
        this.friendApplications = this.friendApplications.filter(item => item.fromUserID !== fromUserID);
        this.actionInfo = '已拒绝好友申请';
        await this.refreshFriendApplications({ force: true, silent: true });
        return true;
      } catch (error) {
        this.actionError = error.message || '拒绝好友申请失败';
        return false;
      } finally {
        this.applicationBusyIds = this.applicationBusyIds.filter(item => item !== busyId);
      }
    },
    async updateContactAlias(contactId, alias) {
      const session = useSessionStore();
      const targetId = String(contactId || '').trim();
      const nextAlias = String(alias || '').trim();
      this.actionError = '';
      this.actionInfo = '';
      if (!session.userId) {
        this.actionError = '请先登录';
        return false;
      }
      if (!targetId) {
        this.actionError = '缺少联系人ID';
        return false;
      }

      try {
        const savedAlias = await imClient.updateContactAlias(session.userId, targetId, nextAlias);
        this.contacts = normalizeContacts(this.contacts.map(item => {
          if (item.uid !== targetId && item.id !== targetId) {
            return item;
          }
          const rawUser = {
            ...(item.rawUser || {}),
            friendAlias: savedAlias
          };
          const rawFriend = {
            ...(item.rawFriend || {}),
            alias: savedAlias
          };
          const title = savedAlias || rawUser.displayName || rawUser.name || item.uid;
          return {
            ...item,
            title,
            avatarText: title.slice(0, 1).toUpperCase(),
            alias: savedAlias,
            rawFriend,
            rawUser
          };
        }));
        this.loadedAt = Date.now();
        this.actionInfo = '备注名已修改';
        return true;
      } catch (error) {
        this.actionError = error.message || '备注名修改失败';
        return false;
      }
    },
    clearContacts() {
      this.refreshSerial += 1;
      this.contacts = [];
      this.activeId = '';
      this.loading = false;
      this.refreshPromise = null;
      this.loadedForUserId = '';
      this.loadedAt = 0;
      this.friendApplications = [];
      this.applicationsLoading = false;
      this.applicationBusyIds = [];
      this.applicationsLoadedForUserId = '';
      this.applicationsLoadedAt = 0;
      this.lastAddContactResult = null;
      this.actionError = '';
      this.actionInfo = '';
    },
    setActiveContact(id) {
      this.activeId = id;
    },
    openChat(contact) {
      if (!contact) {
        return;
      }
      const conversations = useConversationStore();
      const navigation = useNavigationStore();
      const conversation = imClient.createSingleConversation(contact.uid, contact);
      conversations.upsertConversation(conversation);
      conversations.setActiveConversation(conversation.id);
      navigation.showMessages();
    }
  }
});
